import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { API_BASE_URL } from './api-base';

@Injectable({ providedIn: 'root' })
export class ControlchatService {
  private readonly baseUrl = `${API_BASE_URL}/controlchat`;

  constructor(private http: HttpClient) {}

  getUsers(tenantId: number, userId: number, query = '') {
    let params = new HttpParams()
      .set('tenant_id', tenantId)
      .set('user_id', userId);
    if (query) {
      params = params.set('query', query);
    }
    return this.http.get<any[]>(`${this.baseUrl}/users`, { params });
  }

  getConversations(tenantId: number, userId: number) {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.get<any[]>(`${this.baseUrl}/conversations`, { params });
  }

  getMessages(tenantId: number, userId: number, conversationId: number) {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.get<any[]>(`${this.baseUrl}/conversations/${conversationId}/messages`, { params });
  }

  markConversationRead(tenantId: number, userId: number, conversationId: number) {
    return this.http.post(`${this.baseUrl}/conversations/${conversationId}/read`, {
      tenant_id: tenantId,
      user_id: userId
    });
  }

  createDirectConversation(tenantId: number, userId: number, otherUserId: number) {
    return this.http.post<any>(`${this.baseUrl}/conversations/direct`, {
      tenant_id: tenantId,
      user_id: userId,
      other_user_id: otherUserId
    });
  }

  createGroupConversation(tenantId: number, userId: number, name: string, members: number[], description = '') {
    return this.http.post<any>(`${this.baseUrl}/conversations/group`, {
      tenant_id: tenantId,
      user_id: userId,
      name,
      description,
      members
    });
  }

  addGroupMember(tenantId: number, userId: number, conversationId: number, memberId: number) {
    return this.http.post<any>(`${this.baseUrl}/conversations/${conversationId}/members`, {
      tenant_id: tenantId,
      user_id: userId,
      member_id: memberId
    });
  }

  deleteGroup(tenantId: number, userId: number, conversationId: number) {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.delete<any>(`${this.baseUrl}/conversations/${conversationId}/group`, { params });
  }

  getGroupMembers(tenantId: number, userId: number, conversationId: number) {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.get<any[]>(`${this.baseUrl}/conversations/${conversationId}/members`, { params });
  }

  updateGroupMemberRole(tenantId: number, userId: number, conversationId: number, memberId: number, role: string) {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.put(`${this.baseUrl}/conversations/${conversationId}/members/${memberId}/role`, { role }, { params });
  }

  updateGroupSettings(tenantId: number, userId: number, conversationId: number, settings: { only_admins: number }) {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.put(`${this.baseUrl}/conversations/${conversationId}/settings`, settings, { params });
  }

  removeGroupMember(tenantId: number, userId: number, conversationId: number, memberId: number) {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.delete(`${this.baseUrl}/conversations/${conversationId}/members/${memberId}`, { params });
  }

  sendMessage(
    tenantId: number,
    userId: number,
    conversationId: number,
    message: string,
    file?: File | null,
    replyToMessageId?: number | null,
    audioDurationSeconds?: number | null
  ) {
    const formData = new FormData();
    formData.append('tenant_id', String(tenantId));
    formData.append('user_id', String(userId));
    formData.append('conversation_id', String(conversationId));
    if (replyToMessageId) {
      formData.append('reply_to_message_id', String(replyToMessageId));
    }
    if (audioDurationSeconds && Number.isFinite(audioDurationSeconds)) {
      formData.append('audio_duration_seconds', String(audioDurationSeconds));
    }
    if (message) {
      formData.append('message', message);
    }
    if (file) {
      formData.append('file', file);
    }
    return this.http.post<any>(`${this.baseUrl}/messages`, formData);
  }

  toggleReaction(tenantId: number, userId: number, messageId: number, emoji: string) {
    return this.http.post<any>(`${this.baseUrl}/messages/${messageId}/reactions`, {
      tenant_id: tenantId,
      user_id: userId,
      emoji
    });
  }

  getKeywords(tenantId: number, userId: number) {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.get<any[]>(`${this.baseUrl}/keywords`, { params });
  }

  createKeyword(tenantId: number, userId: number, phrase: string) {
    return this.http.post<any>(`${this.baseUrl}/keywords`, {
      tenant_id: tenantId,
      user_id: userId,
      phrase
    });
  }

  deleteKeyword(tenantId: number, userId: number, keywordId: number) {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.delete<any>(`${this.baseUrl}/keywords/${keywordId}`, { params });
  }

  getMonitoring(tenantId: number, userId: number) {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.get<any[]>(`${this.baseUrl}/monitoring`, { params });
  }
}
